import React, { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import USERS, { getUserDataWithUsername, IUserProps } from '../common/data/userDummyData';
import authService, { IAuthUser } from '../services/authService';
import { getImageUrl } from '../helpers/helpers';
import { initPushNotifications } from '../services/firebaseNotification';

export interface IAuthContextProps {
	user: string;
	setUser?(...args: unknown[]): unknown;
	userData: Partial<IUserProps>;
	authUser?: IAuthUser | null;
	setAuthUser?(user: IAuthUser | null): void;
	refetchMe?(): Promise<IAuthUser | null>;
	isLoading?: boolean;
}
const AuthContext = createContext<IAuthContextProps>({} as IAuthContextProps);

interface IAuthContextProviderProps {
	children: ReactNode;
}
export const AuthContextProvider: FC<IAuthContextProviderProps> = ({ children }) => {
	// ENSURE NO USER DATA RESIDES IN LOCALSTORAGE / SESSIONSTORAGE
	try {
		localStorage.removeItem('stichmaster_user');
		localStorage.removeItem('user');
		localStorage.removeItem('facit_user');
		sessionStorage.removeItem('stichmaster_user');
		sessionStorage.removeItem('user');
	} catch (e) {
		// IGNORE
	}

	const [user, setUser] = useState<string>('');
	const [userData, setUserData] = useState<Partial<IUserProps>>({});
	const [authUser, setAuthUser] = useState<IAuthUser | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const applyUserData = useCallback((currentUser: IAuthUser | null, fallbackUsername: string) => {
		if (currentUser) {
			const rawPath =
				currentUser.profileImageUrl ||
				currentUser.profileImage ||
				currentUser.profile_image;
			const avatarUrl = getImageUrl(rawPath, USERS.JOHN.src);
			setUserData({
				...USERS.JOHN,
				src: avatarUrl,
				srcSet: avatarUrl,
				username: currentUser.username,
				name: currentUser.name || currentUser.username,
				surname: '',
				position: currentUser.role?.name || 'Administrator',
				email: currentUser.email,
			});
			return;
		}

		if (fallbackUsername) {
			const found = getUserDataWithUsername(fallbackUsername);
			if (found) {
				setUserData(found);
			} else {
				setUserData({
					...USERS.JOHN,
					username: fallbackUsername,
					name: fallbackUsername,
					surname: '',
					position: 'Administrator',
				});
			}
		} else {
			setUserData({});
		}
	}, []);

	// REFETCH ME DETAILS FROM SERVER
	const refetchMe = useCallback(async (): Promise<IAuthUser | null> => {
		const token = authService.getToken();
		if (!token) {
			setIsLoading(false);
			return null;
		}
		try {
			const me = await authService.getMe();
			if (me) {
				setAuthUser(me);
				setUser(me.username || me.name);
				applyUserData(me, me.username || me.name);
				setIsLoading(false);
				return me;
			}
		} catch {
			// ERRORS HANDLED BY APICLIENT INTERCEPTOR
		}
		setIsLoading(false);
		return null;
	}, [applyUserData]);

	// INITIALIZE PUSH NOTIFICATIONS WHEN AUTHENTICATED
	useEffect(() => {
		if (authUser) {
			const token = authService.getToken();
			if (token) {
				initPushNotifications(token).catch((err) => {
					console.error('Failed to init push notifications:', err);
				});
			}
		}
	}, [authUser]);

	// FETCH ME ON INITIAL LOAD IF TOKEN EXISTS
	useEffect(() => {
		const token = authService.getToken();
		if (token) {
			refetchMe();
		} else {
			setIsLoading(false);
		}
	}, [refetchMe]);

	const handleSetUser = useCallback((newUsername: any) => {
		const usernameStr = typeof newUsername === 'string' ? newUsername : '';
		setUser(usernameStr);
		if (!usernameStr) {
			setAuthUser(null);
			setUserData({});
		}
	}, []);

	const handleSetAuthUser = useCallback(
		(userObj: IAuthUser | null) => {
			setAuthUser(userObj);
			if (userObj) {
				const usernameStr = userObj.username || userObj.name;
				setUser(usernameStr);
				applyUserData(userObj, usernameStr);
			} else {
				setUser('');
				setUserData({});
			}
		},
		[applyUserData],
	);

	const value = useMemo(
		() => ({
			user,
			setUser: handleSetUser,
			userData,
			authUser,
			setAuthUser: handleSetAuthUser,
			refetchMe,
			isLoading,
		}),
		[user, handleSetUser, userData, authUser, handleSetAuthUser, refetchMe, isLoading],
	);
	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
AuthContextProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export default AuthContext;
